// frontend/shared-ui/src/components/Gallery.jsx
import React, { useMemo, useState } from "react";
import Lightbox from "./Lightbox";

/**
 * images: [{ url, thumbnailUrl, originalName }]
 * onRemove: (image, idx) => void
 * onMakePrimary: (image, idx) => void  — moves the image to index 0
 * onReorder: (fromIdx, toIdx) => void  — optional drag-reorder
 */
export default function Gallery({
  images = [],
  onRemove,
  onMakePrimary,
  onReorder,
  emptyMessage = "No images yet",
}) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = (idx) => {
    setLightboxIndex(idx);
    setLightboxOpen(true);
  };

  const handleDelete = (img, idx) => {
    if (!window.confirm("Delete this image?")) return;
    onRemove?.(img, idx);
    // Close lightbox if we deleted the last image
    if (images.length - 1 <= 0) {
      setLightboxOpen(false);
    } else if (idx >= images.length - 1) {
      setLightboxIndex(images.length - 2);
    }
  };

  // -------- Drag reorder --------
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const onDragStart = (e, idx) => {
    if (!onReorder) return;
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    // Firefox requires setData
    e.dataTransfer.setData("text/plain", String(idx));
  };
  const onDragOver = (e, idx) => {
    if (!onReorder || dragIdx === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (idx !== overIdx) setOverIdx(idx);
  };
  const onDrop = (e, idx) => {
    e.preventDefault();
    if (!onReorder || dragIdx === null || dragIdx === idx) {
      setDragIdx(null);
      setOverIdx(null);
      return;
    }
    onReorder(dragIdx, idx);
    setDragIdx(null);
    setOverIdx(null);
  };
  const onDragEnd = () => {
    setDragIdx(null);
    setOverIdx(null);
  };

  const count = images.length;

  if (count === 0) {
    return (
      <div className="fh-gallery__empty">
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <path d="M21 15l-5-5L5 21" />
        </svg>
        <div className="mt-2">{emptyMessage}</div>
      </div>
    );
  }

  return (
    <>
      <div className="fh-gallery">
        {images.map((img, idx) => (
          <div
            key={img.id || img.uuid || img.url || idx}
            className={[
              "fh-gallery__item",
              dragIdx === idx ? "is-dragging" : "",
              overIdx === idx && dragIdx !== idx ? "is-over" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            draggable={!!onReorder}
            onDragStart={(e) => onDragStart(e, idx)}
            onDragOver={(e) => onDragOver(e, idx)}
            onDrop={(e) => onDrop(e, idx)}
            onDragEnd={onDragEnd}
          >
            {/* Thumbnail — click opens lightbox */}
            <button
              type="button"
              className="fh-gallery__thumb"
              onClick={() => openLightbox(idx)}
              aria-label={`Open image ${idx + 1}`}
            >
              <img
                src={img.thumbnailUrl || img.url}
                alt={img.originalName || `image ${idx + 1}`}
                draggable={false}
                loading="lazy"
              />

              {/* Cover badge on first image */}
              {idx === 0 && <div className="fh-gallery__badge">Cover</div>}

              {/* Hover overlay */}
              <div className="fh-gallery__hover">
                <div className="fh-gallery__zoom-icon">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    <line x1="11" y1="8" x2="11" y2="14" />
                    <line x1="8" y1="11" x2="14" y2="11" />
                  </svg>
                </div>
              </div>
            </button>

            {/* Actions toolbar */}
            <div className="fh-gallery__actions">
              {onMakePrimary && idx !== 0 && (
                <button
                  type="button"
                  className="fh-gallery__action"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMakePrimary(img, idx);
                  }}
                  title="Set as cover"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  >
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                </button>
              )}
              {onRemove && (
                <button
                  type="button"
                  className="fh-gallery__action fh-gallery__action--danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(img, idx);
                  }}
                  title="Delete image"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  >
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                    <line x1="10" y1="11" x2="10" y2="17" />
                    <line x1="14" y1="11" x2="14" y2="17" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox */}
      <Lightbox
        open={lightboxOpen}
        images={images}
        index={lightboxIndex}
        onClose={() => setLightboxOpen(false)}
        onIndexChange={setLightboxIndex}
        onDelete={onRemove ? handleDelete : undefined}
      />
    </>
  );
}
